'use client';

import { useTranslation } from '@/hooks/useTranslation';
import { IntegrationBlog } from '@/constants/Integrations';
import Custom404 from '@/app/[locale]/[...not_found]/404';
import LouiseButton from '@/components/buttons/LouiseButton';
import CTABanner from '@/components/blocks/CTABanner';
import { AnimatedGradientWrapper } from '@/components/background/AnimatedGradientWrapper';

interface PageContentProps {
  locale: string;
  name: string;
}

export default function PageContent({ locale, name }: PageContentProps) {
  const { t } = useTranslation(locale);
  if (!name || !IntegrationBlog.some((dr) => dr.name.toLocaleLowerCase() == name.toLocaleLowerCase())) {
    return <Custom404 locale={locale} />;
  }

  const item = IntegrationBlog.find((dr) => dr.name.toLocaleLowerCase() == name.toLocaleLowerCase());

  return (
    <AnimatedGradientWrapper>
      <section className="tw-relative tw-container tw-mx-auto tw-px-4 tw-py-6 tw-max-w-7xl ">
        <div className="tw-container tw-mx-auto tw-px-4 sm:tw-px-6 lg:tw-px-8 tw-max-w-7xl">
          <div className="tw-flex tw-items-center tw-justify-center tw-mb-16 sm:tw-mb-20 lg:tw-mb-24">
            <div className="tw-w-full tw-max-w-3xl tw-flex tw-flex-col tw-justify-start tw-items-start tw-gap-8 sm:tw-gap-10 lg:tw-gap-12">
              <div className="tw-w-full tw-flex tw-flex-col tw-justify-start tw-items-start tw-gap-6 sm:tw-gap-8">
                <div className="tw-w-full tw-flex tw-flex-col tw-justify-start tw-items-start tw-gap-4 sm:tw-gap-5">
                  <h1 className="tw-w-full tw-text-gray-700 tw-text-2xl sm:tw-text-3xl lg:tw-text-4xl tw-font-semibold tw-font-['Lexend'] tw-leading-tight sm:tw-leading-normal lg:tw-leading-10">
                    {t(item?.title ?? '')}
                  </h1>
                  <figure className="tw-w-full tw-aspect-video sm:tw-aspect-[16/10] lg:tw-aspect-[780/428] tw-overflow-hidden tw-rounded-lg">
                    <img
                      className="tw-w-full tw-h-full tw-object-cover"
                      src={item?.imageUrl}
                      alt={t(item?.title ?? 'Image')}
                    />
                  </figure>
                </div>

                <header className="tw-w-full tw-flex tw-flex-col tw-justify-start tw-items-start tw-gap-4 sm:tw-gap-6">
                  <div className="tw-w-full tw-flex tw-flex-col tw-justify-start tw-items-start tw-gap-3 tw-py-2 sm:tw-py-4">
                    <h2 className="tw-w-full tw-text-gray-700 tw-text-xl sm:tw-text-2xl tw-font-semibold tw-font-['Lexend'] tw-leading-relaxed sm:tw-leading-loose">
                      {t(item?.shortDescription.title ?? '')}
                    </h2>
                    <p className="tw-w-full tw-text-gray-700 tw-text-sm sm:tw-text-base tw-font-normal tw-font-['Lexend'] tw-leading-relaxed sm:tw-leading-normal">
                      {t(item?.shortDescription.description ?? '')}
                    </p>
                  </div>
                  <div className="tw-w-full tw-flex tw-justify-start">
                    <LouiseButton color="blue" cta={t('SiteNavbar/l-04')} ctaTarget="register" locale={locale} />
                  </div>
                </header>

                <main className="tw-w-full tw-flex tw-flex-col tw-justify-start tw-items-start tw-gap-4 sm:tw-gap-6 tw-py-2 sm:tw-py-4">
                  <div className="tw-w-full tw-flex tw-flex-col tw-justify-start tw-items-start tw-gap-3">
                    <h2 className="tw-w-full tw-text-gray-700 tw-text-xl sm:tw-text-2xl tw-font-semibold tw-font-['Lexend'] tw-leading-relaxed sm:tw-leading-loose">
                      {t(item?.longDescription.title ?? '')}
                    </h2>
                    <p className="tw-w-full tw-text-gray-700 tw-text-sm sm:tw-text-base tw-font-normal tw-font-['Lexend'] tw-leading-relaxed sm:tw-leading-normal">
                      {t(item?.longDescription.description ?? '')}
                    </p>
                  </div>
                </main>
              </div>
            </div>
          </div>
        </div>

        <CTABanner
          title={t('Home/ReadyToGetStarted/cta-title')}
          description={t('Home/ReadyToGetStarted/cta-description')}
          cta={t('Home/ReadyToGetStarted/start-trial-aria')}
          ctaTarget="register"
          locale={locale}
        />
      </section>
    </AnimatedGradientWrapper>
  );
}
